import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import CustomDialog from '@/components/ui/Dialog/Dialog';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import { putAuth } from '@/api/ApiCalls';
import { queryClient } from '@/hooks/QueryClient';
import { fundIsraeliIdChangeRequestsUrl } from '@/hooks/useFundIsraeliIdChangeRequests';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { isValidIsraeliID } from '@/helpers/israeliIdValidator';
import { formatDateLocale } from '@/helpers/dateHelper';
import { SupportedIsraeliIdChangeRequestDtoIn } from '@/models/in/SupportedIsraeliIdChangeRequestDtoIn';
import styles from './RequestIsraeliIdChangeDialog.module.scss';

interface RequestIsraeliIdChangeDialogProps {
  fundId: number;
  supportedId: number;
  supportedName: string;
  currentIsraeliId: string;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  /** Called after a request is filed or withdrawn, so whatever shows its status can refresh. */
  onRequested?: (request: SupportedIsraeliIdChangeRequestDtoIn | null) => void;
}

interface RequestBody {
  requestedIsraeliId: string;
  note: string;
}

const serverMessageOf = (err: unknown) =>
  axios.isAxiosError(err) && typeof err.response?.data === 'string' ? err.response.data.trim() : '';

/**
 * HAT-1428: the coordinator asks the office to correct a supported's Israeli ID. Druk: the office
 * makes the change, after a request from the coordinator - so this files a request and changes
 * nothing yet.
 *
 * Two states. A request already waiting: say so, and offer to withdraw it (a wrong number typed
 * again is fixed by withdrawing and asking anew). Otherwise: a clean form. The office's decision
 * shows on the supported until the coordinator acknowledges it, and comes by mail - not here.
 */
const RequestIsraeliIdChangeDialog = ({
  fundId, supportedId, supportedName, currentIsraeliId, open, setOpen, onRequested,
}: RequestIsraeliIdChangeDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const [israeliId, setIsraeliId] = useState('');
  const [note, setNote] = useState('');
  // Everything about the number shows under its field; only a failure with no reason goes below
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const requestUrl = `funds/${fundId}/supporteds/${supportedId}/israeli-id-change-request`;
  // 204 when there was never a request - axios hands that back as an empty string
  const { data: latest, refetch } = useGetAuthApi<SupportedIsraeliIdChangeRequestDtoIn | ''>(open ? requestUrl : '');
  const pendingRequest = latest && latest.status === 'Pending' ? latest : null;

  const { mutate: fileRequest, isPending: isSending } = usePostAuthApi<SupportedIsraeliIdChangeRequestDtoIn, RequestBody>(requestUrl);

  useEffect(() => {
    if (open) {
      setIsraeliId('');
      setNote('');
      setFieldError(null);
      setGeneralError(null);
    }
  }, [open]);

  const handleSubmit = () => {
    if (!israeliId.trim()) {
      setFieldError(t('israeliIdChangeRequest.israeliIdRequired'));
      return;
    }
    if (!isValidIsraeliID(israeliId)) {
      setFieldError(t('israeliIdChangeRequest.invalidIsraeliId'));
      return;
    }

    setFieldError(null);
    setGeneralError(null);
    fileRequest(
      { requestedIsraeliId: israeliId.trim().padStart(9, '0'), note: note.trim() },
      {
        onSuccess: (request) => {
          onRequested?.(request);
          // The label on the list, the line in the details and the count all read the fund's open requests
          void queryClient.invalidateQueries({ queryKey: [fundIsraeliIdChangeRequestsUrl(fundId)] });
          void refetch();
          setOpen(false);
        },
        onError: (err) => {
          // The server's reasons are all about this number or this supported's request
          const serverMessage = serverMessageOf(err);
          if (serverMessage) {
            setFieldError(serverMessage);
          } else {
            setGeneralError(t('israeliIdChangeRequest.errorSending'));
          }
        },
      }
    );
  };

  const withdraw = async () => {
    setIsCancelling(true);
    setGeneralError(null);
    try {
      await putAuth(`${requestUrl}/cancel`, { Authorization: `Bearer ${currentUser?.token}` });
      onRequested?.(null);
      void queryClient.invalidateQueries({ queryKey: [fundIsraeliIdChangeRequestsUrl(fundId)] });
      // The form comes up in its place - withdrawing is usually to send another number
      await refetch();
    } catch (err) {
      setGeneralError(serverMessageOf(err) || t('israeliIdChangeRequest.cancelError'));
    } finally {
      setIsCancelling(false);
    }
  };

  if (pendingRequest) {
    return (
      <CustomDialog
        open={open}
        setOpen={setOpen}
        btnText="close"
        onBtnClick={() => setOpen(false)}
        secondaryBtnText={isCancelling ? 'israeliIdChangeRequest.cancelling' : 'israeliIdChangeRequest.cancelRequest'}
        secondaryBtnDisabled={isCancelling}
        onSecondaryBtnClick={withdraw}
      >
        <div className={styles.wrapper}>
          <h3 className={`heading-h4-semibold ${styles.title}`}>{t('israeliIdChangeRequest.pendingTitle')}</h3>
          <div className={styles.pendingCard}>
            <span className={`body-md-semibold ${styles.pendingName}`}>{supportedName}</span>
            <span className={styles.ids}>
              <bdi className={styles.oldId}>{pendingRequest.currentIsraeliId}</bdi>
              <span aria-hidden="true">←</span>
              <bdi className={styles.newId}>{pendingRequest.requestedIsraeliId}</bdi>
            </span>
            <span className={`body-sm-regular ${styles.hint}`}>
              {t('israeliIdChangeRequest.pendingSince', { date: formatDateLocale(pendingRequest.requestedAt) })}
            </span>
          </div>
          <p className={`body-sm-regular ${styles.hint}`}>{t('israeliIdChangeRequest.pendingHint')}</p>
          {generalError && <p className={`body-sm-regular ${styles.error}`} role="alert">{generalError}</p>}
        </div>
      </CustomDialog>
    );
  }

  return (
    <CustomDialog
      open={open}
      setOpen={setOpen}
      btnText={isSending ? 'israeliIdChangeRequest.sending' : 'israeliIdChangeRequest.send'}
      btnDisabled={isSending}
      onBtnClick={handleSubmit}
    >
      <div className={styles.wrapper}>
        <h3 className={`heading-h4-semibold ${styles.title}`}>{t('israeliIdChangeRequest.title')}</h3>
        <p className={`body-md-regular ${styles.subtitle}`}>
          {t('israeliIdChangeRequest.subtitle', { name: supportedName })}
        </p>

        <div className={styles.field}>
          <span className={`body-sm-regular ${styles.label}`}>{t('israeliIdChangeRequest.currentIsraeliId')}</span>
          <bdi className={`body-md-regular ${styles.currentId}`}>{currentIsraeliId}</bdi>
        </div>

        <div className={styles.field}>
          <label htmlFor="requestedIsraeliId" className={`body-sm-regular ${styles.label}`}>
            {t('israeliIdChangeRequest.newIsraeliId')}
          </label>
          <input
            id="requestedIsraeliId"
            type="text"
            inputMode="numeric"
            maxLength={9}
            dir="ltr"
            className={`${styles.input} ${styles.idInput} ${fieldError ? styles.hasError : ''}`}
            value={israeliId}
            onChange={(e) => {
              setIsraeliId(e.target.value.replace(/\D/g, ''));
              if (fieldError) setFieldError(null);
            }}
          />
          {fieldError && <p className={`body-sm-regular ${styles.error}`} role="alert">{fieldError}</p>}
        </div>

        <div className={styles.field}>
          <label htmlFor="israeliIdChangeNote" className={`body-sm-regular ${styles.label}`}>
            {t('israeliIdChangeRequest.note')}
          </label>
          <textarea
            id="israeliIdChangeNote"
            rows={2}
            maxLength={500}
            className={styles.input}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <p className={`body-sm-regular ${styles.hint}`}>{t('israeliIdChangeRequest.hint')}</p>

        {generalError && <p className={`body-sm-regular ${styles.error}`} role="alert">{generalError}</p>}
      </div>
    </CustomDialog>
  );
};

export default RequestIsraeliIdChangeDialog;
